/**
 * PaginationService
 *
 * A reusable, object-oriented service for managing pagination state and navigation.
 * Follows Service-Oriented Design patterns for modularity and reusability.
 *
 * Features:
 * - Track current page and total items
 * - Calculate offset for server-side queries
 * - Navigate next/previous pages
 * - Determine if next/previous are available
 * - Support dynamic page sizes
 */


interface PaginationServiceOptions {
    pageSize?: number;
}

interface PaginationStats {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    startItem: number;
    endItem: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

/**
 * PaginationService class for managing table pagination
 */
export class PaginationService {
    private currentPage: number;
    private pageSize: number;
    private totalItems: number;
    private totalPages: number;

    /**
     * Create a PaginationService instance
     * @param tableId - Unique identifier for this pagination context
     * @param options - Configuration options
     */
    constructor(tableId: string, options: PaginationServiceOptions = {}) {
        this.currentPage = 1; // 1-indexed
        this.pageSize = options.pageSize || 20;
        this.totalItems = 0;
        this.totalPages = 0;

        console.log(`PaginationService initialized for '${tableId}' with page size ${this.pageSize}`);
    }

    /**
     * Set the total number of items
     * @param total - Total number of items in the dataset
     */
    public setTotalItems(total: number): void {
        this.totalItems = total;
        this.totalPages = Math.ceil(total / this.pageSize);

        // Ensure current page is valid
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = this.totalPages;
        }

        console.log(`Pagination: ${total} total items, ${this.totalPages} pages`);
    }

    /**
     * Set the page size
     * @param size - Number of items per page
     */
    public setPageSize(size: number): void {
        this.pageSize = size;
        this.currentPage = 1; // Reset to first page
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        console.log(`Page size changed to ${size}`);
    }

    /**
     * Get the current offset for server-side queries
     * @returns Offset (0-indexed)
     */
    public getOffset(): number {
        return (this.currentPage - 1) * this.pageSize;
    }

    /**
     * Get the current limit for server-side queries
     * @returns Limit (page size)
     */
    public getLimit(): number {
        return this.pageSize;
    }

    /**
     * Navigate to the next page
     * @returns True if navigation was successful
     */
    public nextPage(): boolean {
        if (this.hasNext()) {
            this.currentPage++;
            console.log(`Next page: ${this.currentPage} of ${this.totalPages}`);
            return true;
        }
        console.log('Already on last page');
        return false;
    }

    /**
     * Navigate to the previous page
     * @returns True if navigation was successful
     */
    public previousPage(): boolean {
        if (this.hasPrevious()) {
            this.currentPage--;
            console.log(`Previous page: ${this.currentPage} of ${this.totalPages}`);
            return true;
        }
        console.log('Already on first page');
        return false;
    }

    /**
     * Go to a specific page
     * @param page - Page number (1-indexed)
     * @returns True if navigation was successful
     */
    public goToPage(page: number): boolean {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            console.log(`Go to page: ${this.currentPage} of ${this.totalPages}`);
            return true;
        }
        console.log(`Invalid page number: ${page}`);
        return false;
    }

    /**
     * Check if there is a next page
     * @returns True if there is a next page
     */
    public hasNext(): boolean {
        return this.currentPage < this.totalPages;
    }

    /**
     * Check if there is a previous page
     * @returns True if there is a previous page
     */
    public hasPrevious(): boolean {
        return this.currentPage > 1;
    }

    /**
     * Get the current page number
     * @returns Current page (1-indexed)
     */
    public getCurrentPage(): number {
        return this.currentPage;
    }

    /**
     * Get the total number of pages
     * @returns Total pages
     */
    public getTotalPages(): number {
        return this.totalPages;
    }

    /**
     * Get pagination statistics
     * @returns Pagination stats
     */
    public getStats(): PaginationStats {
        const startItem = this.getOffset() + 1;
        const endItem = Math.min(this.getOffset() + this.pageSize, this.totalItems);

        return {
            currentPage: this.currentPage,
            totalPages: this.totalPages,
            pageSize: this.pageSize,
            totalItems: this.totalItems,
            startItem: startItem,
            endItem: endItem,
            hasNext: this.hasNext(),
            hasPrevious: this.hasPrevious()
        };
    }

    /**
     * Get a formatted display string for pagination info
     * @returns Display string (e.g., "Showing 21-40 of 100")
     */
    public getDisplayString(): string {
        const stats = this.getStats();
        if (this.totalItems === 0) {
            return 'No items';
        }
        return `Showing ${stats.startItem}-${stats.endItem} of ${stats.totalItems}`;
    }

    /**
     * Reset pagination to first page
     */
    public reset(): void {
        this.currentPage = 1;
        console.log('Pagination reset to page 1');
    }

    /**
     * Get pagination configuration (compatible with PaginationConfig type)
     * @returns Pagination configuration object
     */
    public getConfig(): PaginationConfig {
        return {
            currentPage: this.currentPage,
            itemsPerPage: this.pageSize,
            totalItems: this.totalItems,
            totalPages: this.totalPages
        };
    }
}

// Export to global scope
declare global {
    interface Window {
        PaginationService: typeof PaginationService;
    }
}

if (typeof window !== 'undefined') {
    window.PaginationService = PaginationService;
}

console.log('PaginationService loaded');

